// Date labels and per-project hour aggregation for weekly completion charts.
import { format } from 'date-fns';
import { getTaskProjects } from '@/features/tasks/utils/data/taskTags';
import { getCapacityDurationMinutes } from '@/features/tasks/utils/scheduling/taskSchedulingUtils';
import { translate as t } from '@/i18n/useT';
import {
  getDateFnsLocale,
  getShortWeekdayPattern,
  stripDateFnsTrailingPeriod,
} from '@/i18n/getDateFnsLocale';

export const WEEKLY_DAY_RANGE = 8;
export const MS_PER_DAY = 24 * 60 * 60 * 1000;
export const VISIBLE_BARS_DESKTOP = 8;

/** Normaliseer datum naar YYYY-MM-DD string (lokale tijdzone). */
export const toDateStr = (val) => {
  if (!val) return null;
  const d = val.toDate ? val.toDate() : new Date(val);
  if (isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Build array oldest → range end (rightmost = last day in range). */
export const buildDayRange = (n, endDate) => {
  const days = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(endDate);
    d.setDate(d.getDate() - i);
    days.push({ dateStr: toDateStr(d), date: d });
  }
  return days;
};

const capitalizeLabel = (s) =>
  stripDateFnsTrailingPeriod(s).replace(/^./, (c) => c.toUpperCase());

/** Dag-label: vandaag, gisteren, of gelokaliseerde weekdag. */
export const formatDayLabel = (date, dateStr, todayStr, yesterdayStr) => {
  if (dateStr === todayStr) return t('todayviewcontent.today', 'Vandaag');
  if (dateStr === yesterdayStr) return t('dates.yesterday', 'Gisteren');
  return capitalizeLabel(
    format(date, 'EEEE d MMM', { locale: getDateFnsLocale() }),
  );
};

/** Korte staafdag: "Vandaag" of weekdag-afkorting. */
export const formatBarDay = (date, dateStr, todayStr) => {
  if (dateStr === todayStr) return t('todayviewcontent.today', 'Vandaag');
  return capitalizeLabel(
    format(date, getShortWeekdayPattern(), { locale: getDateFnsLocale() }),
  );
};

/** Hoeveel 8-dagen-periodes terug kunnen we nog op basis van oudste voltooiing. */
export const getMaxWeekOffset = (earliestCompletedStr, todayStr) => {
  if (!earliestCompletedStr || !todayStr) return 0;
  const start = new Date(`${earliestCompletedStr}T12:00:00`);
  const end = new Date(`${todayStr}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  const spanDays = Math.floor((end - start) / MS_PER_DAY) + 1;
  return Math.max(0, Math.ceil(spanDays / WEEKLY_DAY_RANGE) - 1);
};

/** Normaliseer ingeplande datum naar lokale YYYY-MM-DD. */
export const normalizeScheduleDate = (val) => {
  if (!val) return null;
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  return toDateStr(val);
};

export const NO_PROJECT_KEY = '__no_project__';

export const buildProjectHours = (
  tasks,
  countingTasks,
  map,
  projectDisplayMap,
) => {
  /** @type {Record<string, { name: string, hours: number }>} */
  const projectHours = {};
  let totalHours = 0;
  tasks.forEach((task) => {
    const minutes = getCapacityDurationMinutes(task, countingTasks, map);
    if (!minutes) return;
    const hrs = minutes / 60;
    totalHours += hrs;
    const projectIds = getTaskProjects(task);
    if (projectIds.length === 0) {
      const name = t('taskmetrics.noProject', 'Geen project');
      if (!projectHours[NO_PROJECT_KEY]) {
        projectHours[NO_PROJECT_KEY] = { name, hours: 0 };
      }
      projectHours[NO_PROJECT_KEY].hours += hrs;
      return;
    }
    projectIds.forEach((pid) => {
      const projectId = String(pid).trim().toLowerCase();
      const name = projectDisplayMap?.get(projectId) || projectId;
      if (!projectHours[projectId]) {
        projectHours[projectId] = { name, hours: 0 };
      }
      projectHours[projectId].hours += hrs;
    });
  });
  return { projectHours, totalHours };
};
